import { useMemo } from 'react';
import { QueryFunctionContext, useInfiniteQuery } from 'react-query';

interface useSearchProductQueryProps {
  rowsPerPage: number;
  queryFn: (context: QueryFunctionContext<any>) => Promise<any>;
  searchParams: ProductsSearch;
}

const useSearchProductQuery = ({ rowsPerPage, queryFn, searchParams }: useSearchProductQueryProps) => {
  const queryKey = ['product-search', searchParams];
  const { data, isLoading, isFetching, isError, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery<any>(queryKey, queryFn, {
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage) return undefined;
        const count = lastPage.data?.length ?? 0;
        const nextPage = allPages.length + 1;
        return count === 0 || count < rowsPerPage ? undefined : nextPage;
      },
      cacheTime: Infinity,
      staleTime: Infinity,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  const products = useMemo(() => {
    if (!data?.pages?.length) return [];
    return data.pages.flatMap((page) => page?.data ?? []);
  }, [data]);

  const totalCounts = useMemo(() => {
    const totalCount = data?.pages[0]?.totalElements;
    return totalCount;
  }, [data]);

  return {
    data,
    products,
    totalCounts,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  };
};

export default useSearchProductQuery;
